// Types and Interfaces
export interface Signer {
  uuid: string;
  email: string;
  signed: boolean;
  timestamp: string | null;
  timestampUTC: string | null;
  signerFullName: string;
  timestampLocal: string | null;
  GPS?: string;
  location?: string;
  sourceIp?: string;
  timezone?: string;
  deviceUsed?: string;
  postalCode?: string;
  additionalDetails?: {
    keyAlias: string;
    Signature: string;
    digestHash: string;
    signingAlgorithm: string;
  };
}

export interface ContractLevel {
  levelId: number;
  signers: Signer[];
  isCompleted: boolean;
  isLastLevel: boolean;
  signedSoFar: number;
  requiredSignatures: number;
}

export interface ContractDetails {
  levels: ContractLevel[];
  contractName?: string;
  originatorRole: string;
  originatorEmail: string;
  documentTemplate: string;
  originatorUserName: string;
}

export interface Contract {
  contract_id: string;
  contract_status: string;
  contract_details: ContractDetails;
  contract_start_date_utc: string;
}

export interface ContractsResponse {
  username: string;
  contracts: Contract[];
  available_balance: string;
  currency: string | null;
  ds_fee: string;
  aes_fee: string;
}

// Contract Status Enum
export enum ContractStatus {
  COMPLETE = 'Complete',
  IN_PROGRESS = 'In Progress',
  INITIATED = 'Initiated',
  PENDING = 'Pending',
  CANCELLED = 'Cancelled',
}

// Business Logic Functions

/**
 * Get the color for a contract status chip
 */
export const getStatusColor = (
  status: string
): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case ContractStatus.COMPLETE:
      return 'success';
    case ContractStatus.IN_PROGRESS:
      return 'warning';
    case ContractStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Truncate contract ID for display
 */
export const truncateContractId = (
  contractId: string,
  length: number = 8
): string => {
  if (contractId.length <= length) {
    return contractId;
  }
  return `${contractId.substring(0, length)}...`;
};

/**
 * Get total number of signers across all levels
 */
export const getTotalSigners = (contract: Contract): number => {
  return contract.contract_details.levels.reduce(
    (total, level) => total + level.signers.length,
    0
  );
};

/**
 * Get total number of signed signers across all levels
 */
export const getTotalSignedSigners = (contract: Contract): number => {
  return contract.contract_details.levels.reduce(
    (total, level) =>
      total + level.signers.filter((signer) => signer.signed).length,
    0
  );
};

/**
 * Get total required signatures across all levels
 */
export const getTotalRequiredSignatures = (contract: Contract): number => {
  return contract.contract_details.levels.reduce(
    (total, level) => total + level.requiredSignatures,
    0
  );
};

/**
 * Check if a contract is fully signed
 */
export const isContractFullySigned = (contract: Contract): boolean => {
  const totalSigned = getTotalSignedSigners(contract);
  const totalRequired = getTotalRequiredSignatures(contract);
  return totalSigned >= totalRequired;
};

/**
 * Get signing progress percentage
 */
export const getSigningProgress = (contract: Contract): number => {
  const totalSigned = getTotalSignedSigners(contract);
  const totalRequired = getTotalRequiredSignatures(contract);
  if (totalRequired === 0) return 0;
  return Math.round((totalSigned / totalRequired) * 100);
};

/**
 * Get current level information
 */
export const getCurrentLevel = (contract: Contract): ContractLevel | null => {
  return (
    contract.contract_details.levels.find((level) => !level.isCompleted) || null
  );
};

/**
 * Get next level information
 */
export const getNextLevel = (contract: Contract): ContractLevel | null => {
  const currentLevel = getCurrentLevel(contract);
  if (!currentLevel) return null;

  const currentIndex = contract.contract_details.levels.findIndex(
    (level) => level.levelId === currentLevel.levelId
  );

  return contract.contract_details.levels[currentIndex + 1] || null;
};

/**
 * Format currency display
 */
export const formatCurrency = (
  amount: string,
  currency: string | null
): string => {
  const currencySymbol = currency || 'USD';
  return `${amount} ${currencySymbol}`;
};

/**
 * Get contract summary information
 */
export const getContractSummary = (contract: Contract) => {
  return {
    id: contract.contract_id,
    status: contract.contract_status,
    documentTemplate: contract.contract_details.documentTemplate,
    originator: contract.contract_details.originatorUserName,
    totalSigners: getTotalSigners(contract),
    signedSigners: getTotalSignedSigners(contract),
    requiredSignatures: getTotalRequiredSignatures(contract),
    progress: getSigningProgress(contract),
    isComplete: isContractFullySigned(contract),
  };
};

/**
 * Sort contracts by status and date
 */
export const sortContracts = (contracts: Contract[]): Contract[] => {
  return [...contracts].sort((a, b) => {
    // First sort by status priority
    const statusPriority: Record<string, number> = {
      [ContractStatus.IN_PROGRESS]: 1,
      [ContractStatus.INITIATED]: 2,
      [ContractStatus.PENDING]: 3,
      [ContractStatus.COMPLETE]: 4,
      [ContractStatus.CANCELLED]: 5,
    };

    const aPriority = statusPriority[a.contract_status] || 6;
    const bPriority = statusPriority[b.contract_status] || 6;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same status, sort by contract ID (newest first)
    return b.contract_id.localeCompare(a.contract_id);
  });
};

/**
 * Filter contracts by status
 */
export const filterContractsByStatus = (
  contracts: Contract[],
  status: ContractStatus | 'all'
): Contract[] => {
  if (status === 'all') {
    return contracts;
  }
  return contracts.filter((contract) => contract.contract_status === status);
};

/**
 * Search contracts by text
 */
export const searchContracts = (
  contracts: Contract[],
  searchTerm: string
): Contract[] => {
  if (!searchTerm.trim()) {
    return contracts;
  }

  const term = searchTerm.toLowerCase();
  return contracts.filter(
    (contract) =>
      contract.contract_id.toLowerCase().includes(term) ||
      contract.contract_details.documentTemplate.toLowerCase().includes(term) ||
      contract.contract_details.originatorUserName
        .toLowerCase()
        .includes(term) ||
      contract.contract_details.originatorEmail.toLowerCase().includes(term)
  );
};

/**
 * Get contract statistics
 */
export const getContractStatistics = (contracts: Contract[]) => {
  const stats = {
    total: contracts.length,
    complete: 0,
    inProgress: 0,
    initiated: 0,
    pending: 0,
    cancelled: 0,
  };

  contracts.forEach((contract) => {
    switch (contract.contract_status) {
      case ContractStatus.COMPLETE:
        stats.complete++;
        break;
      case ContractStatus.IN_PROGRESS:
        stats.inProgress++;
        break;
      case ContractStatus.INITIATED:
        stats.initiated++;
        break;
      case ContractStatus.PENDING:
        stats.pending++;
        break;
      case ContractStatus.CANCELLED:
        stats.cancelled++;
        break;
    }
  });

  return stats;
};

/**
 * Validate contract data
 */
export const validateContract = (contract: Contract): boolean => {
  return !!(
    contract.contract_id &&
    contract.contract_status &&
    contract.contract_details &&
    contract.contract_details.documentTemplate &&
    contract.contract_details.originatorUserName
  );
};

/**
 * Get readable status text
 */
export const getStatusText = (status: string): string => {
  switch (status) {
    case ContractStatus.COMPLETE:
      return 'Completed';
    case ContractStatus.IN_PROGRESS:
      return 'In Progress';
    case ContractStatus.INITIATED:
      return 'Initiated';
    case ContractStatus.PENDING:
      return 'Pending';
    case ContractStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};
